import { OvFormContext, OvFormInput } from '@components'
import { LABELS, TOOLTIPS } from '@constants'
import { formatValueToCurrency, roundNumber } from '@helpers/formatCentToAmount.helper'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { DeleteOutlineOutlined, Inventory } from '@mui/icons-material'
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { GridExpandMoreIcon } from '@mui/x-data-grid'
import { ISingleProduct, IVatCode } from '@typings'
import { observer } from 'mobx-react-lite'
import { useContext, useMemo, useState } from 'react'
import { useWatch, useFormContext } from 'react-hook-form'
import { ProductPickerModal } from 'src/partials/modals/products/productPicker.modal'

interface IDocumentSingleLineProps {
  [key: string]: any
  index: number
  removeLine: (index: number) => void
  filteredVatCodes?: IVatCode[]
  id?: number
  vat_code_id?: number
  quantity?: number
  price?: number
  label?: string
  description?: string | null
}

export const DocumentSingleLine = observer(
  ({ index, id, vat_code_id, quantity, price, label, removeLine, filteredVatCodes }: IDocumentSingleLineProps) => {
    const { shared, company } = useStore()
    const isMobile = useIsMobile()
    const formContext = useContext(OvFormContext)
    const form = useFormContext()
    const [productPickerOpen, setProductPickerOpen] = useState(false)

    // Use filtered VAT codes if provided, otherwise use all codes
    const availableVatCodes = filteredVatCodes || shared.current_vat_codes

    // Check if company is tax payable (hide VAT when not)
    const isCompanyTaxPayable = company.current_company?.tax_payable ?? true

    // Watch the actual form values for this line to calculate local totals for display
    const watchedQuantity = useWatch({
      control: form.control,
      name: `lines.${index}.quantity`,
      defaultValue: quantity,
    })
    const watchedPrice = useWatch({
      control: form.control,
      name: `lines.${index}.price`,
      defaultValue: price,
    })
    const watchedVatCodeId = useWatch({
      control: form.control,
      name: `lines.${index}.vat_code_id`,
      defaultValue: vat_code_id,
    })
    const watchedDescription = useWatch({
      control: form.control,
      name: `lines.${index}.description`,
    })

    // Determine if description field should be shown
    const showDescription = !formContext.readOnly || (watchedDescription && watchedDescription.trim() !== '')

    // Calculate line totals for display
    const total = useMemo(() => {
      const qty = Number(watchedQuantity) || 0
      const prc = Number(watchedPrice) || 0
      const totalExVat = qty * prc

      // If company not tax payable or no VAT code, VAT is 0
      const vatPercentage = watchedVatCodeId
        ? availableVatCodes.find((code: IVatCode) => code.id === watchedVatCodeId)?.percentage || 0
        : 0
      const totalVat = vatPercentage ? roundNumber(totalExVat * vatPercentage) / 100 : 0
      const total = totalExVat + totalVat

      return {
        totalExVat,
        totalVat,
        total,
      }
    }, [watchedQuantity, watchedPrice, watchedVatCodeId, availableVatCodes])

    const handleProductSelect = (product: ISingleProduct) => {
      // Populate form fields with product data
      form.setValue(`lines.${index}.label`, product.name)
      form.setValue(`lines.${index}.description`, product.description ?? null)
      form.setValue(`lines.${index}.price`, product.unit_price || 0)
      form.setValue(`lines.${index}.quantity`, 1)
      // Keep existing VAT code or use default
    }

    return isMobile ? (
      <>
        <Accordion
          key={id}
          component={'div'}
          sx={{
            mb: 1,
            '&:before': { display: 'none' },
            boxShadow: 1,
            borderRadius: 1,
          }}>
          <AccordionSummary
            expandIcon={<GridExpandMoreIcon color="primary" />}
            sx={{
              padding: 2,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              flex={1}>
              <Typography fontWeight={500}>{label || 'Nieuwe regel'}</Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary">
                {formatValueToCurrency(total.total)}
              </Typography>
            </Stack>
          </AccordionSummary>
          {!formContext.readOnly && (
            <AccordionActions sx={{ px: 2, pb: 1, justifyContent: 'space-between' }}>
              <Tooltip title={TOOLTIPS.SELECT_FROM_CATALOG}>
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  startIcon={<Inventory />}
                  onClick={() => setProductPickerOpen(true)}>
                  {LABELS.SELECT_FROM_CATALOG}
                </Button>
              </Tooltip>
              <Tooltip title={TOOLTIPS.DELETE_DOCUMENT_LINE}>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  endIcon={<DeleteOutlineOutlined />}
                  onClick={() => removeLine(index)}>
                  Regel verwijderen
                </Button>
              </Tooltip>
            </AccordionActions>
          )}
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <OvFormInput
                name={`lines.${index}.label`}
                label={LABELS.TITLE}
              />
              {showDescription && (
                <OvFormInput
                  name={`lines.${index}.description`}
                  label={LABELS.EXTRA_DESCRIPTION}
                  placeholder="Omschrijving..."
                  multiline
                  minRows={2}
                  maxRows={8}
                />
              )}
              <OvFormInput
                type="currency"
                name={`lines.${index}.price`}
                label={LABELS.PRICE}
              />
              <OvFormInput
                label={LABELS.QUANTITY}
                name={`lines.${index}.quantity`}
                inputProps={{ type: 'number', min: 0 }}
                type="number"
              />
              {isCompanyTaxPayable && (
                <OvFormInput
                  size="small"
                  label={LABELS.VAT}
                  name={`lines.${index}.vat_code_id`}
                  type="vat"
                />
              )}
              <Stack
                direction="row"
                justifyContent="space-between">
                <Typography variant="body2">{LABELS.SUB_TOTAL}</Typography>
                <Typography variant="h5">{formatValueToCurrency(total.totalExVat)}</Typography>
              </Stack>
              {isCompanyTaxPayable && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between">
                    <Typography variant="body2">{LABELS.VAT}</Typography>
                    <Typography variant="h5">{formatValueToCurrency(total.totalVat) ?? '-'}</Typography>
                  </Stack>
                  <Divider variant="middle" />
                  <Stack
                    direction="row"
                    justifyContent="space-between">
                    <Typography variant="body2">{LABELS.TOTAL}</Typography>
                    <Typography variant="h5">{formatValueToCurrency(total.total)}</Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <ProductPickerModal
          open={productPickerOpen}
          onClose={() => setProductPickerOpen(false)}
          onSelect={handleProductSelect}
        />
      </>
    ) : (
      <>
        <TableRow
          sx={{
            '&:hover': {
              bgcolor: 'action.hover',
            },
            transition: 'background-color 0.2s',
          }}>
          <TableCell sx={{ verticalAlign: 'top' }}>
            <OvFormInput
              size="small"
              readOnly={formContext.readOnly}
              name={`lines.${index}.label`}
              placeholder="Titel"
              boldValue={true}
            />
            {showDescription && (
              <OvFormInput
                size="small"
                readOnly={formContext.readOnly}
                name={`lines.${index}.description`}
                placeholder="Omschrijving (optioneel)"
                multiline
                minRows={1}
                maxRows={6}
                sx={{
                  mt: 1,
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputBase-input': {
                    '&::placeholder': {
                      fontSize: '0.8125rem',
                    }
                  }
                }}
              />
            )}
          </TableCell>
          <TableCell sx={{ width: '120px', verticalAlign: 'top' }}>
            <OvFormInput
              size="small"
              readOnly={formContext.readOnly}
              name={`lines.${index}.quantity`}
              inputProps={{ type: 'number', min: 0 }}
              type="number"
            />
          </TableCell>
          <TableCell sx={{ width: '140px', verticalAlign: 'top' }}>
            <OvFormInput
              size="small"
              readOnly={formContext.readOnly}
              type="currency"
              name={`lines.${index}.price`}
            />
          </TableCell>
          {isCompanyTaxPayable && (
            <TableCell sx={{ width: '180px', verticalAlign: 'top' }}>
              <OvFormInput
                size="small"
                label=""
                name={`lines.${index}.vat_code_id`}
                type="vat"
              />
            </TableCell>
          )}
          <TableCell sx={{ width: '140px', verticalAlign: 'top' }}>
            <Typography
              variant="h5"
              fontWeight={600}>
              {formatValueToCurrency(total.totalExVat)}
            </Typography>
          </TableCell>
          {!formContext.readOnly && (
            <TableCell sx={{ width: '120px', verticalAlign: 'top' }}>
              <Tooltip title={TOOLTIPS.SELECT_FROM_CATALOG}>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setProductPickerOpen(true)}
                  sx={{ minWidth: 'auto', p: 1, mr: 0.5 }}>
                  <Inventory fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title={TOOLTIPS.DELETE_DOCUMENT_LINE}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeLine(index)}
                  sx={{ minWidth: 'auto', p: 1 }}>
                  <DeleteOutlineOutlined fontSize="small" />
                </Button>
              </Tooltip>
            </TableCell>
          )}
        </TableRow>

        <ProductPickerModal
          open={productPickerOpen}
          onClose={() => setProductPickerOpen(false)}
          onSelect={handleProductSelect}
        />
      </>
    )
  }
)
